import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Customer } from '@/types/customer';
import { useToast } from '@/hooks/use-toast';
import { useProfile } from './useProfile';
import { auditService } from '@/services/auditService';

export const useCustomers = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('customers')
        .select('*, created_by, updated_by, updated_at, deleted_at, deleted_by')
        .is('deleted_at', null)
        .eq('active', true)
        .order('name');

      if (error) throw error;
      return (data || []) as Customer[];
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const createMutation = useMutation({
    mutationFn: async (customer: Partial<Customer>) => {
      const { data, error } = await supabase
        .from('customers')
        .insert([customer as any])
        .select()
        .single();

      if (error) throw error;
      return data as Customer;
    },
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      
      await auditService.logAction({
        action: 'CRIAÇÃO',
        module: 'CADASTROS',
        entityType: 'cliente',
        entityId: data.id,
        description: `Cliente criado: ${data.name}`,
        details: {
          name: data.name,
          document_number: data.document_number
        }
      });
      
      toast({ title: 'Cliente criado com sucesso' });
    },
    onError: (error) => {
      toast({
        title: 'Erro ao criar cliente',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { data: customer, error: customerFetchError } = await supabase
        .from('customers')
        .select('name, document_number')
        .eq('id', id)
        .single();

      if (customerFetchError) throw customerFetchError;
      
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase
        .from('customers')
        .update({ deleted_at: new Date().toISOString(), deleted_by: user?.id })
        .eq('id', id);
      
      if (error) throw error;
      return customer;
    },
    onSuccess: async (customer) => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      
      await auditService.logAction({
        action: 'EXCLUSÃO',
        module: 'CADASTROS',
        entityType: 'cliente',
        description: `Cliente excluído: ${customer?.name}`,
        details: {
          name: customer?.name,
          document_number: customer?.document_number
        }
      });
      
      toast({ title: 'Cliente excluído com sucesso' });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao excluir cliente',
        description: error.message,
        variant: 'destructive',
      });
    }
  });

  return {
    customers: query.data || [],
    isLoading: query.isLoading,
    createCustomer: createMutation.mutate,
    isCreating: createMutation.isPending,
    deleteCustomer: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
  };
};

export const useCustomersBySalesperson = () => {
  const { toast } = useToast();
  const { data: profile, isLoading: isLoadingProfile } = useProfile();

  const query = useQuery({
    queryKey: ['customers', 'by-salesperson', profile?.user_id],
    queryFn: async () => {
      if (!profile?.user_id) {
        return [];
      }

      const { data, error } = await supabase
        .from('customers')
        .select('*, created_by, updated_by, updated_at, deleted_at, deleted_by')
        .is('deleted_at', null)
        .eq('active', true)
        .eq('salesperson_id', profile.user_id)
        .order('name');

      if (error) throw error;
      return (data || []) as Customer[];
    },
    enabled: !!profile?.user_id,
  });

  return {
    customers: query.data || [],
    isLoading: query.isLoading || isLoadingProfile,
  };
};
